// @flow

import { useState, useCallback, useEffect, useRef } from 'react';

type FormState = {
  [key: string]: any
};

type ValidationFunction = (value: any, formState?: FormState) => boolean;

export type Validation = ValidationFunction | RegExp;

function isFunction(arg: Validation): boolean %checks {
  return typeof arg === 'function';
}

const _validate = (
  value: any,
  validations: Validation[],
  formState?: FormState
): boolean =>
  validations.every((validation) =>
    isFunction(validation)
      ? validation(value, formState)
      : validation.test(value)
  );

type Props = {
  validations: Validation[],
  validateOnSet?: boolean,
  shouldValidate: Function,
  value: any,
  updateFormState: Function,
  formState?: FormState
};

export type Field = {
  value: any,
  set: (value: any) => void,
  isValid: boolean,
  validate: () => any,
  hasBeenSet: ?boolean,
  success: ?boolean,
  error: ?boolean
};

// useField must only be used in conjunction with useForm. Fields belong as a part of a form context and need to know about their adjacent fields
export const useField = ({
  value,
  validateOnSet,
  shouldValidate = () => true,
  validations,
  updateFormState,
  formState
}: Props): Field => {
  const hasValue = value !== undefined && value !== null;
  value = value ?? '';
  const [hasBeenSet, setHasBeenSet] = useState(false);

  // Keeping track of whether the hook was mounted or dismounted allow us to prevent
  // lingering state updates on an unmounted component
  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const shouldRunValidations = shouldValidate(value, formState);

  // only run validations when we shouldValidate to handle conditional validation cases
  const isValid = shouldRunValidations
    ? _validate(value, validations, formState)
    : true;

  const [hasValidationSucceeded, setValidationSucceeded] = useState(
    hasValue && shouldRunValidations ? isValid : undefined
  );

  const set = useCallback(
    (v: any) => {
      if (!hasBeenSet && mounted.current === true) {
        setHasBeenSet(true);
      }

      mounted.current === true && updateFormState && updateFormState(v);
    },
    [hasBeenSet, setHasBeenSet, updateFormState]
  );

  const validate = useCallback(
    () =>
      shouldRunValidations &&
      mounted.current === true &&
      setValidationSucceeded(isValid),
    [isValid, shouldRunValidations]
  );

  useEffect(() => {
    if (validateOnSet && hasBeenSet) {
      validate();
    }
  }, [value, validate, hasBeenSet, validateOnSet]);

  return {
    value: value ?? '',
    set,
    isValid,
    validate,
    hasBeenSet,
    success: hasValidationSucceeded,
    error:
      hasValidationSucceeded === undefined
        ? hasValidationSucceeded
        : !hasValidationSucceeded
  };
};
